import i18n from "i18next";
import { getIntlLocale } from "util/locale";
/** Helpers for KPI View redesign — score, delta, grouping, catalog. */

/** Ant Design 5 Select search — option.children is not a plain string. */
export function filterSelectOption(input, option) {
  const label = option?.label ?? option?.title ?? option?.children ?? "";
  return String(label).toLowerCase().includes(String(input).toLowerCase());
}

/** metricList API: custom_metrics, general_metrics (NPS/Rating), ai_topics (AI labels). */
export function mergeMetricListLabels(metricListSuccess) {
  if (!metricListSuccess) return [];
  const custom = metricListSuccess.custom_metrics || [];
  const general = metricListSuccess.general_metrics || [];
  const aiTopics = (metricListSuccess.ai_topics || []).map((t) =>
    typeof t === "string" ? t : t?.topic_name ?? t?.label ?? ""
  );
  const seen = new Set();
  const merged = [];
  [...custom, ...general, ...aiTopics].forEach((label) => {
    const key = String(label ?? "").trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(key);
  });
  return merged;
}

export function resolveSourceSortTime(source) {
  const raw =
    source?.last_update ?? source?.LastUpdate ?? source?.lastUpdate ?? null;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

export function formatSourceLastUpdate(source) {
  const raw =
    source?.last_update ?? source?.LastUpdate ?? source?.lastUpdate ?? null;
  if (!raw) return "";
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function sortSourcesByLastUpdate(sources = []) {
  return [...sources].sort((a, b) => {
    const diff = resolveSourceSortTime(b) - resolveSourceSortTime(a);
    if (diff !== 0) return diff;
    return String(a?.Name ?? "").localeCompare(String(b?.Name ?? ""), getIntlLocale(i18n.language));
  });
}

export function formatKpiCreateSubtopicNotice(result, t) {
  const labels = Array.isArray(result?.subtopic_labels)
    ? result.subtopic_labels.map((label) => String(label || "").trim()).filter(Boolean)
    : (Array.isArray(result?.subtopics) ? result.subtopics : [])
        .map((row) => String(row?.label || "").trim())
        .filter(Boolean);
  const count = Number(result?.subtopic_count) || labels.length;
  if (!count) return null;
  const shown = labels.slice(0, 8);
  const names = shown.join(", ") + (labels.length > 8 ? "…" : "");
  return t("team_kpi_view.subtopics_created", {
    count,
    names,
    defaultValue:
      "{{count}} subtopics are included on the same card: {{names}}.",
  });
}

export function resolveKpiSubtopics(item) {
  const raw = item?.subtopics;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((row) => {
    const id = row?.id ?? row?.topic_id;
    const label = String(row?.label ?? row?.name ?? "").trim();
    if (id == null || !label) return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      id,
      label,
      declined: Boolean(row?.declined),
    });
  });
  return out;
}

export function parseKpiMetrics(item) {
  const raw = item?.metric;
  if (Array.isArray(raw)) {
    return raw.map((m) => String(m ?? "").trim()).filter(Boolean);
  }
  if (raw != null && String(raw).trim()) {
    return [String(raw).trim()];
  }
  return [];
}

export function parseKpiDashboardIds(item) {
  const raw = item?.dashboard_ids;
  if (Array.isArray(raw)) {
    return raw.filter((id) => id != null && id !== "");
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((id) => id != null && id !== "")
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function resolveKpiTitle(item) {
  if (!item) return "";
  if (item.name) return String(item.name);
  const metricParts = Array.isArray(item.metric)
    ? item.metric.map((m) => String(m ?? "").trim()).filter(Boolean)
    : [String(item.metric ?? "").trim()].filter(Boolean);
  if (metricParts.length) return metricParts.join(" ");
  const pivots = resolveKpiPivots(item);
  if (pivots.length) {
    return pivots.map((p) => `${p.key}: ${p.value}`).join(" · ");
  }
  return "KPI";
}

/** Alert create API expects `source` as str (dashboard_ids payload), not name list. */
export function resolveKpiAlertSourceForApi(item) {
  const raw = item?.dashboard_ids;
  if (typeof raw === "string" && raw.trim()) return raw;
  if (Array.isArray(raw) && raw.length) return JSON.stringify(raw);
  const ids = parseKpiDashboardIds(item);
  if (ids.length) return JSON.stringify(ids);
  if (typeof item?.source === "string" && item.source.trim()) return item.source;
  if (Array.isArray(item?.source) && item.source.length) {
    return JSON.stringify(item.source);
  }
  return "";
}

export function resolveKpiAlertIdForApi(item) {
  const raw = item?.id ?? item?.kpi_id;
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolveKpiAlertMetricsForApi(item) {
  const raw = item?.metric;
  if (Array.isArray(raw)) {
    return raw.map((m) => String(m).trim()).filter(Boolean);
  }
  if (raw != null && String(raw).trim()) return [String(raw).trim()];
  return [];
}

/** Alert create payloads: parent KPI + optional selected subtopic scopes (same kpi_id). */
export function buildKpiAlertCreatePlans(
  item,
  includeSubtopics = false,
  selectedSubtopicKeys = null,
) {
  const kpiId = resolveKpiAlertIdForApi(item);
  const source = resolveKpiAlertSourceForApi(item);
  const parentMetrics = resolveKpiAlertMetricsForApi(item);
  const parentTitle = resolveKpiTitle(item);
  if (!kpiId || !source || !parentMetrics.length) {
    return [];
  }
  const plans = [
    {
      id: kpiId,
      source,
      alert_title: parentTitle,
      metrics: parentMetrics,
    },
  ];
  if (!includeSubtopics) {
    return plans;
  }

  const selectedSet =
    selectedSubtopicKeys == null
      ? null
      : new Set(
          (Array.isArray(selectedSubtopicKeys) ? selectedSubtopicKeys : []).map(
            (key) => String(key),
          ),
        );

  resolveKpiSubtopics(item).forEach((subtopic) => {
    const label = String(subtopic?.label ?? "").trim();
    if (!label) return;
    const subtopicKey = String(subtopic?.id ?? label);
    if (selectedSet != null && !selectedSet.has(subtopicKey)) {
      return;
    }
    plans.push({
      id: kpiId,
      source,
      alert_title: `${parentTitle} · ${label}`,
      metrics: [label],
    });
  });
  return plans;
}

/** Alert recipients from top_data `alert.send_to`. */
export function resolveKpiAlertRecipients(item) {
  const sendTo = item?.alert?.send_to;
  if (Array.isArray(sendTo)) {
    return sendTo.map((v) => String(v).trim()).filter(Boolean);
  }
  return [];
}

export function isKpiAlertEnabled(item) {
  return Boolean(item?.alerted);
}

export function resolveKpiAlertLastCheck(item) {
  const at = item?.alert?.last_checked_at || null;
  const result = item?.alert?.last_check_result;
  return {
    at,
    result: result === "fired" || result === "no_anomaly" ? result : null,
  };
}

export function formatKpiAlertLastCheckedAt(raw) {
  if (!raw) return "";
  const parsed = new Date(raw);
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.toLocaleString(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Pivot chips from API payload (`pivots` or `pivot_filter`). */
export function resolveKpiPivots(item) {
  if (!item) return [];
  if (Array.isArray(item.pivots) && item.pivots.length > 0) {
    return item.pivots
      .map((p) => ({
        key: String(p?.key ?? "").trim(),
        value: String(p?.value ?? "").trim(),
      }))
      .filter((p) => p.key && p.value);
  }
  const rawFilter = item.pivot_filter;
  let filter = rawFilter;
  if (typeof filter === "string" && filter.trim()) {
    try {
      filter = JSON.parse(filter);
    } catch {
      return [];
    }
  }
  if (!filter || typeof filter !== "object") return [];
  const chips = [];
  Object.entries(filter).forEach(([key, values]) => {
    const vals = Array.isArray(values) ? values : [values];
    vals.forEach((value) => {
      const v = String(value ?? "").trim();
      if (key && v) chips.push({ key: String(key), value: v });
    });
  });
  return chips;
}

/** "MARBAY CAVE CAPPADOCIA" → "Marbay Cave Cappadocia" */
export function formatPivotLabel(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Human-readable pivot subtitle (field names omitted). */
export function resolveKpiPivotSubtitle(item) {
  const pivots = resolveKpiPivots(item);
  if (!pivots.length) return null;
  const labels = pivots
    .map((pivot) => formatPivotLabel(pivot.value))
    .filter(Boolean);
  return labels.length ? labels.join(" · ") : null;
}

/** Metric row color from change direction (not absolute value). */
export function resolveKpiMetricDirectionLevel(metricType, direction) {
  if (metricType === "volume") return "neutral";
  if (direction === "up") return "success";
  if (direction === "down") return "danger";
  return "neutral";
}

export function pivotFilterFromPivots(pivots = []) {
  if (!Array.isArray(pivots) || pivots.length === 0) return null;
  const merged = {};
  pivots.forEach((pivot) => {
    const key = String(pivot?.key ?? "").trim();
    const value = String(pivot?.value ?? "").trim();
    if (!key || !value) return;
    if (!merged[key]) merged[key] = [];
    merged[key].push(value);
  });
  return Object.keys(merged).length ? merged : null;
}

/** API payload shape: `{ column: [values] }` for get_kpi_reviews / chartData. */
export function resolveKpiPivotFilterForApi(item) {
  if (!item) return null;
  const raw = item.pivot_filter;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const out = {};
    Object.entries(raw).forEach(([key, values]) => {
      const col = String(key).trim();
      if (!col) return;
      const valList = Array.isArray(values) ? values : [values];
      const clean = valList.map((v) => String(v).trim()).filter(Boolean);
      if (clean.length) out[col] = clean;
    });
    return Object.keys(out).length ? out : null;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return resolveKpiPivotFilterForApi({ pivot_filter: parsed });
      }
    } catch {
      return null;
    }
  }
  const pivots = resolveKpiPivots(item);
  if (!pivots.length) return null;
  const merged = {};
  pivots.forEach(({ key, value }) => {
    if (!merged[key]) merged[key] = [];
    merged[key].push(value);
  });
  return merged;
}

export function resolveKpiScore(item) {
  const changes = item?.last_3_month_change;
  if (!changes || typeof changes !== "object") return null;
  const keys = item?.month_labels?.length
    ? item.month_labels
    : Object.keys(changes);
  if (!keys.length) return null;
  const lastKey = keys[keys.length - 1];
  const raw = changes[lastKey] ?? Object.values(changes).pop();
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function resolveKpiV3(item) {
  return item?.kpi_v3 ?? null;
}

export function resolveSparklineValues(item) {
  return resolveSparklineSeries(item).values;
}

export function resolveSparklineDates(item) {
  return resolveSparklineSeries(item).dates;
}

function buildFallbackSparklineDates(length) {
  if (!length) return [];
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Array.from({ length }, (_, index) => {
    const day = new Date(today);
    day.setDate(today.getDate() - (length - 1 - index));
    return day.toISOString().slice(0, 10);
  });
}

export function resolveSparklineSeries(item) {
  const trend = item?.kpi_v3?.trend;
  const rawValues = trend?.values;
  const bucketDays = Number(trend?.bucket_days);
  const granularity =
    trend?.granularity === "rolling" || trend?.granularity === "weekly"
      ? trend.granularity
      : "daily";
  const resolvedBucketDays = Number.isFinite(bucketDays) && bucketDays > 0
    ? bucketDays
    : granularity === "weekly"
      ? 7
      : granularity === "rolling"
        ? 30
        : 1;
  if (Array.isArray(rawValues) && rawValues.length > 0) {
    const values = rawValues.map((v) => Number(v)).filter((v) => Number.isFinite(v));
    const rawDates = Array.isArray(trend?.dates) ? trend.dates : [];
    const dates =
      rawDates.length === values.length
        ? rawDates.map((d) => String(d).slice(0, 10))
        : buildFallbackSparklineDates(values.length);
    return { values, dates, granularity, bucketDays: resolvedBucketDays };
  }

  const changes = item?.last_3_month_change;
  if (!changes || typeof changes !== "object") {
    return { values: [], dates: [], granularity: "daily", bucketDays: 1 };
  }
  const keys = item?.month_labels?.length
    ? item.month_labels
    : Object.keys(changes);
  const values = keys
    .map((k) => Number(changes[k]))
    .filter((v) => Number.isFinite(v));
  return {
    values,
    dates: buildFallbackSparklineDates(values.length),
    granularity: "daily",
    bucketDays: 1,
  };
}

function formatLocalIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Inclusive calendar range for a sparkline bucket starting at dateStr. */
export function resolveSparklineBucketRange(startStr, bucketDays = 7) {
  const days = Number(bucketDays) > 0 ? Number(bucketDays) : 7;
  const start = new Date(`${String(startStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(start.getTime())) {
    const day = String(startStr).slice(0, 10);
    return { since: day, until: day };
  }
  const end = new Date(start);
  end.setDate(start.getDate() + (days - 1));
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const cappedEnd = end > today ? today : end;
  return { since: formatLocalIsoDate(start), until: formatLocalIsoDate(cappedEnd) };
}

/** ISO week range (Mon–Sun) for sparkline click; current week capped at today. */
export function resolveSparklineWeekRange(weekStartStr) {
  return resolveSparklineBucketRange(weekStartStr, 7);
}

export function formatSparklineAxisDate(dateStr) {
  if (!dateStr) return "";
  const parsed = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(dateStr);
  return parsed.toLocaleDateString(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "short",
  });
}

export function formatSparklineTooltipDate(
  dateStr,
  { granularity = "daily", bucketDays } = {},
) {
  if (!dateStr) return "";
  const parsed = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(dateStr);

  const rangeDays =
    Number(bucketDays) > 0
      ? Number(bucketDays)
      : granularity === "weekly"
        ? 7
        : granularity === "rolling"
          ? 7
          : 1;

  if (granularity === "weekly" || granularity === "rolling") {
    const end = new Date(parsed);
    end.setDate(parsed.getDate() + (rangeDays - 1));
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const cappedEnd = end > today ? today : end;
    const startLabel = parsed.toLocaleDateString(getIntlLocale(i18n.language), {
      day: "numeric",
      month: "long",
    });
    const endLabel = cappedEnd.toLocaleDateString(getIntlLocale(i18n.language), {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    if (parsed.getMonth() === cappedEnd.getMonth() && parsed.getFullYear() === cappedEnd.getFullYear()) {
      return `${parsed.getDate()}–${cappedEnd.getDate()} ${cappedEnd.toLocaleDateString(getIntlLocale(i18n.language), { month: "long", year: "numeric" })}`;
    }
    return `${startLabel} – ${endLabel}`;
  }

  return parsed.toLocaleDateString(getIntlLocale(i18n.language), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Haftalık x ekseni tick'leri (0, 7, 14, … + son gün). */
export function pickSparklineWeeklyTicks(dates = [], intervalDays = 7) {
  if (!dates.length) return [];
  const ticks = [];
  for (let index = 0; index < dates.length; index += intervalDays) {
    ticks.push({ index, date: dates[index] });
  }
  const lastIndex = dates.length - 1;
  if (ticks[ticks.length - 1]?.index !== lastIndex) {
    ticks.push({ index: lastIndex, date: dates[lastIndex] });
  }
  return ticks;
}

export function formatKpiV3Count(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(getIntlLocale(i18n.language));
}

export function formatKpiV3Rating(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

export function formatKpiV3Share(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const formatted = n.toLocaleString(getIntlLocale(i18n.language), {
    minimumFractionDigits: n % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
  return `${formatted}%`;
}

export function resolveKpiV3SharePct(metric, volumeMetric) {
  if (metric?.current_pct != null) return Number(metric.current_pct);
  const total = Number(volumeMetric?.current);
  const part = Number(metric?.current);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(part)) return null;
  return Math.round((part / total) * 1000) / 10;
}

export function resolveKpiV3ShareChange(metric, volumeMetric) {
  if (metric?.change_pp != null && metric?.is_increased != null) {
    const pp = Number(metric.change_pp);
    if (Number.isFinite(pp) && pp === 0) return null;
    return {
      change_pp: metric.change_pp,
      is_increased: metric.is_increased,
    };
  }
  const currentPct = resolveKpiV3SharePct(metric, volumeMetric);
  const previousTotal = Number(volumeMetric?.previous);
  const previousPart = Number(metric?.previous);
  if (
    currentPct == null ||
    !Number.isFinite(previousTotal) ||
    previousTotal <= 0 ||
    !Number.isFinite(previousPart)
  ) {
    return null;
  }
  const previousPct = Math.round((previousPart / previousTotal) * 1000) / 10;
  const delta = Math.round((currentPct - previousPct) * 10) / 10;
  if (delta === 0) return null;
  return {
    change_pp: Math.abs(delta),
    is_increased: delta > 0,
  };
}

export function formatKpiV3ShareChange(metric, volumeMetric) {
  const resolved = resolveKpiV3ShareChange(metric, volumeMetric);
  if (!resolved) return null;
  const sign = resolved.is_increased ? "+" : "-";
  const formatted = Number(resolved.change_pp).toLocaleString(getIntlLocale(i18n.language), {
    minimumFractionDigits: resolved.change_pp % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
  return `${sign}${formatted} p.p.`;
}

export function resolveKpiV3ShareTrendDirection(metric, volumeMetric) {
  const resolved = resolveKpiV3ShareChange(metric, volumeMetric);
  if (!resolved) return "flat";
  return resolved.is_increased ? "up" : "down";
}

function isKpiV3ZeroChange(value) {
  const n = Number(value);
  return Number.isFinite(n) && n === 0;
}

export function formatKpiV3Change(metric, { isRating = false } = {}) {
  if (!metric) return null;
  if (isRating) {
    if (metric.change == null || metric.is_increased == null) return null;
    if (isKpiV3ZeroChange(metric.change)) return null;
    const sign = metric.is_increased ? "+" : "-";
    return `${sign}${metric.change}`;
  }
  if (metric.change_pct == null || metric.is_increased == null) return null;
  if (isKpiV3ZeroChange(metric.change_pct)) return null;
  const sign = metric.is_increased ? "+" : "-";
  return `${sign}${metric.change_pct}%`;
}

export function resolveKpiV3TrendDirection(metric) {
  if (!metric || metric.is_increased == null) return "flat";
  const changeValue = metric.change_pct ?? metric.change;
  if (changeValue == null || isKpiV3ZeroChange(changeValue)) return "flat";
  return metric.is_increased ? "up" : "down";
}

/** Per-metric absolute-value thresholds for KPI card performance colors. */
export const KPI_METRIC_PERFORMANCE_CONFIG = {
  positive: { kind: "higher-is-better", successMin: 85, warningMin: 70 },
  negative: { kind: "lower-is-better", successMax: 15, warningMax: 30 },
  rating: { kind: "higher-is-better", successMin: 4.0, warningMin: 3.5 },
};

export function resolveKpiMetricPerformanceLevel(metricKey, rawValue) {
  const config = KPI_METRIC_PERFORMANCE_CONFIG[metricKey];
  if (!config || rawValue == null) return null;
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return null;

  if (config.kind === "higher-is-better") {
    if (value >= config.successMin) return "success";
    if (value >= config.warningMin) return "warning";
    return "danger";
  }

  if (value <= config.successMax) return "success";
  if (value <= config.warningMax) return "warning";
  return "danger";
}

/** Negatif yorum artışı kötü → kırmızı, azalış iyi → yeşil (ok yönünden bağımsız). */
export function resolveKpiV3NegativeDeltaTone(metric) {
  const direction = resolveKpiV3TrendDirection(metric);
  if (direction === "up") return "down";
  if (direction === "down") return "up";
  return "flat";
}

export function resolveKpiV3NegativeShareDeltaTone(metric, volumeMetric) {
  const direction = resolveKpiV3ShareTrendDirection(metric, volumeMetric);
  if (direction === "up") return "down";
  if (direction === "down") return "up";
  return "flat";
}

const KPI_V3_PERIOD_KEYS = {
  "last 30 days": ["period_last_30", "last 30 days"],
  "previous 30 days": ["period_prev_30", "previous 30 days"],
  "last 7 days": ["period_last_7", "last 7 days"],
  "previous 7 days": ["period_prev_7", "previous 7 days"],
  "last 90 days": ["period_last_90", "last 90 days"],
  "previous 90 days": ["period_prev_90", "previous 90 days"],
};

function localizeKpiV3PeriodLabel(label, t) {
  if (!label) return null;
  const normalized = String(label).trim().toLowerCase();
  const mapped = KPI_V3_PERIOD_KEYS[normalized];
  if (!mapped) return String(label).trim();
  const [key, fallback] = mapped;
  return t ? t(`team_kpi_view.${key}`, fallback) : fallback;
}

export function formatKpiV3Footer(v3, t) {
  const period = localizeKpiV3PeriodLabel(v3?.period_label, t) || (
    t ? t("team_kpi_view.period_last_30", "last 30 days") : "last 30 days"
  );
  const compare = localizeKpiV3PeriodLabel(v3?.compare_label, t);
  if (compare) {
    const suffix = t
      ? t("team_kpi_view.compare_suffix", "comparison")
      : "comparison";
    return `${period} · ${compare} ${suffix}`;
  }
  return period;
}

export function normalizeKpiCompareWindow(period) {
  const key = String(period || "").toUpperCase();
  if (key === "7D" || key === "W") return "7D";
  return "30D";
}

export function toKpiApiPeriod(windowKey) {
  return normalizeKpiCompareWindow(windowKey) === "7D" ? "W" : "30D";
}

export function kpiCompareWindowDays(windowKey) {
  return normalizeKpiCompareWindow(windowKey) === "7D" ? 7 : 30;
}

export function formatKpiCompareFooter(windowKey, t) {
  if (normalizeKpiCompareWindow(windowKey) === "7D") {
    return t
      ? t(
          "team_kpi_view.compare_footer_7",
          "last 7 days · vs previous 7 days",
        )
      : "last 7 days · vs previous 7 days";
  }
  return t
    ? t(
        "team_kpi_view.compare_footer_30",
        "last 30 days · vs previous 30 days",
      )
    : "last 30 days · vs previous 30 days";
}

export function resolveKpiDelta(item) {
  const values = resolveSparklineValues(item);
  if (values.length >= 2) {
    const delta = values[values.length - 1] - values[values.length - 2];
    return Number(delta.toFixed(1));
  }
  const pc = item?.percentage_change;
  if (pc != null && pc !== "No change" && String(pc).trim() !== "") {
    const n = parseFloat(String(pc).replace("%", ""));
    if (Number.isFinite(n)) {
      const sign = item?.is_increased === false ? -1 : 1;
      return Number((n * sign * 0.01 * (resolveKpiScore(item) ?? 1)).toFixed(1));
    }
  }
  return null;
}

export function resolveTrendDirection(item, delta) {
  if (delta != null && delta !== 0) return delta > 0 ? "up" : "down";
  if (item?.is_increased === true) return "up";
  if (item?.is_increased === false) return "down";
  return "flat";
}

export function buildKpiGroups(kpis, pagesSubtitle = []) {
  const items = Array.isArray(kpis) ? kpis : [];
  const subtitles = Array.isArray(pagesSubtitle) ? pagesSubtitle : [];
  const pageCount = Math.max(1, Math.ceil(items.length / 4));

  const groups = [];
  for (let page = 1; page <= pageCount; page += 1) {
    const subtitle = subtitles.find((s) => Number(s.page) === page);
    const slice = items.slice((page - 1) * 4, page * 4);
    groups.push({
      id: `group-${page}`,
      page,
      name: subtitle?.name || "",
      kpis: slice,
      hasEmptySlot: slice.length < 4,
    });
  }

  if (items.length === 0) {
    groups.push({
      id: "group-1",
      page: 1,
      name: subtitles.find((s) => Number(s.page) === 1)?.name || "",
      kpis: [],
      hasEmptySlot: true,
    });
  }

  return groups;
}

export function extractMetricParentChild(label) {
  const text = String(label ?? "").trim();
  if (!text) {
    return { parent: "GENEL", childName: "", fullLabel: "" };
  }
  const parts = text
    .split(">")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length > 1) {
    return {
      parent: parts[0].toUpperCase(),
      childName: parts[parts.length - 1],
      fullLabel: text,
    };
  }
  const firstWord = text.split(/[\s/]/)[0];
  return {
    parent: firstWord ? firstWord.toUpperCase() : "GENEL",
    childName: text,
    fullLabel: text,
  };
}

export function extractMetricCategory(label) {
  return extractMetricParentChild(label).parent;
}

export function buildCatalogEntries(metrics, sources = [], addedIds = new Set()) {
  const sourceMap = {};
  sources.forEach((s) => {
    sourceMap[s.id] = s.Name;
  });

  return (metrics || []).map((entry, index) => {
    const label =
      entry?.label ||
      entry?.name ||
      (typeof entry === "string" ? entry : resolveKpiTitle(entry));
    const id = entry?.id ?? `metric-${index}`;
    const sourceId = entry?.sourceId ?? entry?.dashboardId;
    const { parent, childName, fullLabel } = extractMetricParentChild(label);
    return {
      id,
      label: fullLabel || label,
      childName: childName || label,
      category: parent,
      sourceId,
      sourceLabel: entry?.sourceLabel || sourceMap[sourceId] || "Survey",
      responseCount: null,
      isAdded: addedIds.has(id),
      raw: entry,
    };
  });
}

export function isSurveyDashboardName(name) {
  return /survey/i.test(String(name ?? ""));
}

/** Stable key for topic selection (full metric label). */
export function topicSelectionKey(entry) {
  return String(entry?.label ?? entry?.childName ?? "").trim();
}

export function filterBuilderTopicsBySource(
  entries,
  selectedDashboards,
  filter
) {
  if (filter === "all" || !selectedDashboards.length) return entries;

  const hasSurvey = selectedDashboards.some((d) =>
    isSurveyDashboardName(d.name)
  );
  const hasInternal = selectedDashboards.some(
    (d) => !isSurveyDashboardName(d.name)
  );

  if (filter === "survey" && !hasSurvey) return [];
  if (filter === "internal" && !hasInternal) return [];
  return entries;
}

function maxTopicReviewCount(items = []) {
  let max = -1;
  items.forEach((item) => {
    const n = Number(item?.reviewCount24h);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max;
}

/** Sort topics by 24h review count (desc), then label. Missing counts go last. */
export function compareTopicReviewCountDesc(a, b) {
  const aCount = Number(a?.reviewCount24h);
  const bCount = Number(b?.reviewCount24h);
  const aValid = Number.isFinite(aCount);
  const bValid = Number.isFinite(bCount);
  if (aValid && bValid && aCount !== bCount) return bCount - aCount;
  if (aValid && !bValid) return -1;
  if (!aValid && bValid) return 1;
  return String(a?.childName ?? a?.label ?? "").localeCompare(
    String(b?.childName ?? b?.label ?? ""),
    "tr"
  );
}

export function groupCatalogByCategory(entries) {
  const map = new Map();
  entries.forEach((entry) => {
    const cat = entry.category || "GENEL";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(entry);
  });
  const groups = Array.from(map.entries()).map(([category, items]) => ({
    category,
    items: [...items].sort(compareTopicReviewCountDesc),
  }));
  groups.sort((a, b) => {
    const maxA = maxTopicReviewCount(a.items);
    const maxB = maxTopicReviewCount(b.items);
    if (maxA !== maxB) return maxB - maxA;
    return a.category.localeCompare(b.category, getIntlLocale(i18n.language));
  });
  return groups;
}

/** Group builder topics under level-0 parent labels. */
export function groupTopicsByLevel0(entries) {
  return groupCatalogByCategory(entries);
}

/** Build selectable topic rows from metricList API response. */
export function buildEntriesFromMetricList(
  metricListSuccess,
  selectedDashboards = [],
  reviewCountsByLabel = null
) {
  const labels = mergeMetricListLabels(metricListSuccess);
  const topicIdByName = {};
  (metricListSuccess?.ai_topics || []).forEach((t) => {
    const name = t?.topic_name ?? t?.label;
    if (name != null && t?.topic_id != null) {
      topicIdByName[String(name).trim()] = t.topic_id;
    }
  });

  return labels.map((label, index) => {
    const { parent, childName, fullLabel } = extractMetricParentChild(label);
    const topicKey = fullLabel || label;
    const reviewCount24h =
      reviewCountsByLabel && Object.prototype.hasOwnProperty.call(reviewCountsByLabel, topicKey)
        ? reviewCountsByLabel[topicKey]
        : reviewCountsByLabel && Object.prototype.hasOwnProperty.call(reviewCountsByLabel, label)
        ? reviewCountsByLabel[label]
        : null;
    return {
      id: `topic-${index}-${topicKey}`,
      label: topicKey,
      childName: childName || label,
      category: parent,
      topicKey,
      topicId: topicIdByName[topicKey] ?? topicIdByName[label] ?? null,
      reviewCount24h,
    };
  });
}

/** Subtitle under each topic row in KPI builder. */
export function formatTopicReviewCount24h(count, { loading = false, t } = {}) {
  if (loading) {
    return t
      ? t("team_kpi_view.topic_reviews_loading", "loading...")
      : "loading...";
  }
  if (count == null) return "";
  const n = Number(count);
  if (!Number.isFinite(n)) return "";
  const formatted = n.toLocaleString(getIntlLocale(i18n.language));
  return t
    ? t("team_kpi_view.topic_reviews_24h", {
        count: formatted,
        defaultValue: "{{count}} reviews in the last 24 hours",
      })
    : `${formatted} reviews in the last 24 hours`;
}

export function formatScore(score) {
  if (score == null || !Number.isFinite(score)) return "—";
  return score % 1 === 0 ? String(score) : score.toFixed(1);
}

export function formatDelta(delta) {
  if (delta == null || !Number.isFinite(delta)) return null;
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)}`;
}

export const KPI_METRIC_REVIEW_FILTERS = {
  volume: {},
  positive: { sentiment: "1.0" },
  appraisal: { intent: "1" },
  rating: {},
};

export function resolveKpiMetricReviewFilter(metricKey) {
  return KPI_METRIC_REVIEW_FILTERS[metricKey] || {};
}

export function resolveKpiRollingDayRange(days = 30) {
  const windowDays = Number(days) > 0 ? Number(days) : 30;
  const until = new Date();
  const since = new Date();
  since.setDate(until.getDate() - (windowDays - 1));
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { since: fmt(since), until: fmt(until) };
}

export function resolveKpiRolling30DayRange() {
  return resolveKpiRollingDayRange(30);
}

export function resolveKpiMetricFilterLabel(metricKey, t) {
  switch (metricKey) {
    case "positive":
      return t
        ? t("team_kpi_view.metric_positive", "Positive")
        : "Positive";
    case "appraisal":
      return t?.("welcome.intentAppraisal") || "Appraisal";
    case "rating":
      return t
        ? t("team_kpi_view.metric_rating", "Average rating")
        : "Average rating";
    case "volume":
      return t
        ? t("team_kpi_view.metric_volume", "Total reviews")
        : "Total reviews";
    default:
      return "";
  }
}
